(() => {
  const ROOT_SELECTOR = '.nr-product-ecom';

  function getGallerySlides(gallery) {
    return Array.from(
      gallery.querySelectorAll(
        '.nr-product-ecom__gallery-viewer .product__media-item[data-nr-caption]'
      )
    );
  }

  function getGalleryThumbnails(gallery) {
    return Array.from(
      gallery.querySelectorAll(
        '.nr-product-ecom__thumbnail-slider .thumbnail[data-target], .nr-product-ecom__thumbnail-slider .thumbnail'
      )
    );
  }

  function getCurrentGalleryIndex(gallery) {
    const thumbnails = getGalleryThumbnails(gallery);

    if (thumbnails.length) {
      const currentIndex = thumbnails.findIndex(
        (thumbnail) => thumbnail.getAttribute('aria-current') === 'true'
      );

      if (currentIndex >= 0) return currentIndex;
    }

    const slides = getGallerySlides(gallery);
    const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));

    return activeIndex >= 0 ? activeIndex : 0;
  }

  function syncGalleryCaption(gallery, forcedIndex = null) {
    const captionCurrent = gallery.querySelector(
      '[data-nr-gallery-caption-current]'
    );
    const captionTotal = gallery.querySelector('[data-nr-gallery-caption-total]');
    const captionText = gallery.querySelector('[data-nr-gallery-caption-text]');

    if (!captionCurrent || !captionText) return;

    const slides = getGallerySlides(gallery);
    if (!slides.length) return;

    let currentIndex = Number.isInteger(forcedIndex)
      ? forcedIndex
      : getCurrentGalleryIndex(gallery);

    currentIndex = Math.max(0, Math.min(currentIndex, slides.length - 1));

    const thumbnails = getGalleryThumbnails(gallery);
    let activeSlide = slides[currentIndex] || slides[0];

    if (thumbnails[currentIndex]) {
      const target = thumbnails[currentIndex].dataset.target;
      if (target) {
        activeSlide =
          slides.find((slide) => slide.dataset.mediaId === target) || activeSlide;
      }
    }

    captionCurrent.textContent = String(currentIndex + 1);
    if (captionTotal) captionTotal.textContent = String(slides.length);
    captionText.textContent = activeSlide?.dataset.nrCaption || '';
  }

  function activateGalleryIndex(gallery, nextIndex) {
    const thumbnails = getGalleryThumbnails(gallery);
    if (!thumbnails.length) return;

    const count = thumbnails.length;
    const normalizedIndex = ((nextIndex % count) + count) % count;
    const thumbnail = thumbnails[normalizedIndex];

    // Reuse Dawn's native media-gallery behavior instead of duplicating it.
    thumbnail.click();

    // The native gallery updates aria-current / is-active synchronously in most
    // cases, but two animation frames also cover browser/theme timing differences.
    window.requestAnimationFrame(() => {
      syncGalleryCaption(gallery, normalizedIndex);
      window.requestAnimationFrame(() => syncGalleryCaption(gallery));
    });
  }

  function initGallery(gallery) {
    if (gallery.dataset.nrEcomGalleryInitialized === 'true') return;
    gallery.dataset.nrEcomGalleryInitialized = 'true';

    const thumbnails = getGalleryThumbnails(gallery);
    const prevButton = gallery.querySelector(
      '.nr-product-ecom__gallery-viewer > .slider-buttons .slider-button--prev'
    );
    const nextButton = gallery.querySelector(
      '.nr-product-ecom__gallery-viewer > .slider-buttons .slider-button--next'
    );

    // Dawn's desktop thumbnail layout hides inactive main slides, so its native
    // slider arrows do not have a horizontal track to scroll. For this skin the
    // arrows simply activate the previous/next native thumbnail instead.
    const bindArrow = (button, direction) => {
      if (!button) return;

      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();

          const currentIndex = getCurrentGalleryIndex(gallery);
          activateGalleryIndex(gallery, currentIndex + direction);
        },
        true
      );
    };

    bindArrow(prevButton, -1);
    bindArrow(nextButton, 1);

    // Keep caption and x/y counter synced when Dawn changes the active thumbnail.
    if (thumbnails.length) {
      const thumbnailObserver = new MutationObserver(() => {
        syncGalleryCaption(gallery);
      });

      thumbnails.forEach((thumbnail) => {
        thumbnailObserver.observe(thumbnail, {
          attributes: true,
          attributeFilter: ['aria-current'],
        });
      });
    }

    gallery.addEventListener('click', (event) => {
      const thumbnail = event.target.closest(
        '.nr-product-ecom__thumbnail-slider .thumbnail'
      );

      if (!thumbnail) return;

      const currentIndex = getGalleryThumbnails(gallery).indexOf(thumbnail);

      window.requestAnimationFrame(() => {
        syncGalleryCaption(gallery, currentIndex >= 0 ? currentIndex : null);
        window.requestAnimationFrame(() => syncGalleryCaption(gallery));
      });
    });

    syncGalleryCaption(gallery);
  }

  function loadFrontrowScript(block) {
    if (block.dataset.nrFrontrowInitialized === 'true') return;
    block.dataset.nrFrontrowInitialized = 'true';

    const src = block.dataset.frontrowScriptSrc;
    if (!src) return;

    const alreadyLoaded = Array.from(
      document.querySelectorAll('script[data-nr-frontrow-script]')
    ).some((script) => script.src === new URL(src, window.location.href).href);

    if (alreadyLoaded) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.dataset.nrFrontrowScript = 'true';
    document.head.appendChild(script);
  }

  function showProductFormError(button, message) {
    const productForm = button.closest('product-form');
    const wrapper = productForm?.querySelector(
      '.product-form__error-message-wrapper'
    );
    const messageEl = productForm?.querySelector(
      '.product-form__error-message'
    );

    if (!wrapper || !messageEl) return;

    messageEl.textContent = message || 'Unable to add this item to cart.';
    wrapper.hidden = false;
  }

  function clearProductFormError(button) {
    const productForm = button.closest('product-form');
    const wrapper = productForm?.querySelector(
      '.product-form__error-message-wrapper'
    );

    if (wrapper) wrapper.hidden = true;
  }

  async function handleBuyNow(button) {
    if (button.disabled || button.dataset.nrBusy === 'true') return;

    const form = button.closest('form[action*="/cart/add"]');
    if (!form) return;

    const addUrl = button.dataset.cartAddUrl;
    const checkoutUrl = button.dataset.checkoutUrl || '/checkout';
    if (!addUrl) return;

    clearProductFormError(button);

    button.dataset.nrBusy = 'true';
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');

    const spinner = button.querySelector('.loading__spinner');
    spinner?.classList.remove('hidden');

    try {
      const formData = new FormData(form);

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.status) {
        throw new Error(
          payload.description || payload.message || 'Unable to add this item to cart.'
        );
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      showProductFormError(button, error.message);
      button.dataset.nrBusy = 'false';
      button.removeAttribute('aria-busy');
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  }

  function initWithin(scope = document) {
    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      root
        .querySelectorAll('.nr-product-ecom__gallery')
        .forEach((gallery) => initGallery(gallery));

      root
        .querySelectorAll('[data-nr-frontrow]')
        .forEach((block) => loadFrontrowScript(block));
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-nr-buy-now]');
    if (!button) return;

    event.preventDefault();
    handleBuyNow(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document));
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initWithin(event.target);
  });

  const domObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initWithin(node);
      });
    });
  });

  const observeTarget = document.body || document.documentElement;
  domObserver.observe(observeTarget, { childList: true, subtree: true });
})();
